// src/client/screens/borrower/ProfileScreen.tsx
import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Pencil, Check, X, AlertTriangle, Trash2,
  Shield, Bell, FileText, HelpCircle, ChevronRight,
  LogOut, CreditCard, User as UserIcon, Smartphone, Settings,
} from 'lucide-react';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@client/auth/useAuth';
import { useToast } from '@client/components/ToastProvider';
import { useNotificationPrefs } from '@client/hooks/useNotificationPrefs';
import { useAvatarUpload } from '@client/hooks/useAvatarUpload';
import { usePwaInstall } from '@client/hooks/usePwaInstall';
import { useLoansHistory } from '@client/hooks/useLoan';
import { useCreditScore } from '@client/hooks/useCreditScore';
import NavBar from '@client/components/NavBar';
import Button from '@client/components/Button';
import Pill from '@client/components/Pill';
import SectionLabel from '@client/components/SectionLabel';
import { formatZambiaPhone } from '@client/utils/validation';
import { supabase } from '@client/lib/supabase';
import type { KycStatus } from '@client/lib/fixtures';

const APP_VERSION = '1.0.0';

const KYC_TONE: Record<KycStatus, 'success' | 'warning' | 'danger'> = {
  verified: 'success',
  pending: 'warning',
  manual_review: 'warning',
  rejected: 'danger',
};
const KYC_LABEL: Record<KycStatus, string> = {
  verified: 'Verified',
  pending: 'Pending review',
  manual_review: 'Under review',
  rejected: 'Rejected',
};

const ICON_BTN: React.CSSProperties = {
  background: 'transparent',
  border: '1px solid var(--c-border)',
  width: 38,
  height: 38,
  borderRadius: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--c-text)',
  cursor: 'pointer',
};

interface ToggleProps { checked: boolean; onChange: (v: boolean) => void; label: string; }
function Toggle({ checked, onChange, label }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        width: 44, height: 26, borderRadius: 13,
        border: 'none',
        background: checked ? 'var(--c-accent)' : 'var(--c-border)',
        cursor: 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 3,
        left: checked ? 21 : 3,
        width: 20, height: 20, borderRadius: '50%',
        background: '#FFFFFF',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 0.2s',
      }} />
    </button>
  );
}

interface ListRowProps {
  Icon: React.ElementType;
  label: string;
  detail?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  isLast?: boolean;
}
function ListRow({ Icon, label, detail, onClick, href, isLast }: ListRowProps) {
  const inner = (
    <>
      <span style={{
        width: 32, height: 32, borderRadius: 9,
        background: 'var(--c-surface-alt)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={16} color="var(--c-text)" />
      </span>
      <span style={{
        flex: 1,
        fontSize: 14, color: 'var(--c-text)', fontWeight: 500, textAlign: 'left',
      }}>
        {label}
      </span>
      {detail !== undefined && (
        <span style={{
          fontFamily: typeof detail === 'string' && /^\d+$/.test(detail) ? 'var(--ff-mono)' : 'var(--ff-sys)',
          fontSize: 12,
          color: 'var(--c-text-muted)',
        }}>
          {detail}
        </span>
      )}
      {(onClick || href) && <ChevronRight size={16} color="var(--c-text-dim)" />}
    </>
  );
  const baseStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    padding: '14px 16px',
    background: 'transparent',
    border: 'none',
    borderBottom: isLast ? 'none' : '1px solid var(--c-border-lt)',
    cursor: onClick || href ? 'pointer' : 'default',
    color: 'inherit',
    textDecoration: 'none',
  };
  if (href) {
    return <a href={href} style={baseStyle}>{inner}</a>;
  }
  return (
    <button type="button" onClick={onClick} style={baseStyle}>
      {inner}
    </button>
  );
}

export default function ProfileScreen() {
  const { profile, signOut, refresh, deleteAccount } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { prefs, update: updatePref } = useNotificationPrefs();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { upload, uploading } = useAvatarUpload(profile?.user_id ?? '');
  const { canInstall, isInstalled, install } = usePwaInstall();
  const { data: history } = useLoansHistory();
  const { data: score } = useCreditScore();

  const [avatarUrl, setAvatarUrl] = useState<string | null>(profile?.avatar_url ?? null);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState(profile?.full_name ?? '');
  const [savingName, setSavingName] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Avatar upload
  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await upload(file);
    if (url) {
      setAvatarUrl(url);
      await refresh();
    } else {
      toast.show('Could not upload photo', 'error');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [upload, refresh, toast]);

  // Name save
  const handleSaveName = async () => {
    const trimmed = nameInput.trim();
    if (!trimmed || !profile) return;
    setSavingName(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: trimmed })
        .eq('user_id', profile.user_id);
      if (error) { toast.show('Could not save name', 'error'); return; }
      await refresh();
      setEditingName(false);
      toast.show('Name updated', 'success');
    } finally {
      setSavingName(false);
    }
  };

  // PWA install
  const handleInstall = async () => {
    const outcome = await install();
    if (outcome === 'accepted') toast.show('Thandizo added to your home screen!', 'success');
  };

  // Sign out
  const handleSignOut = async () => {
    setSigningOut(true);
    try { await signOut(); } finally { setSigningOut(false); }
  };

  // Delete account
  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const result = await deleteAccount();
      if (!result.ok) {
        toast.show(result.error ?? 'Could not delete account', 'error');
        setShowDeleteModal(false);
        return;
      }
    } finally {
      setDeleting(false);
    }
  };

  // Derived
  const displayName = profile?.full_name || formatZambiaPhone(profile?.phone) || '—';
  const kycStatus: KycStatus = profile?.kyc_status ?? 'pending';
  const loansCount = (history ?? []).length;
  const onTime = (() => {
    const loans = history ?? [];
    if (!loans.length) return '—';
    const repaid = loans.filter((l) => l.status === 'repaid').length;
    return `${Math.round((repaid / loans.length) * 100)}%`;
  })();

  return (
    <div className="app-shell app-shell--with-nav">
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        aria-hidden="true"
        onChange={handleFileChange}
      />

      <main className="scroll-area" style={{ padding: 0 }}>
        {/* Title + settings */}
        <div style={{
          padding: '20px 24px 0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h1 style={{
            fontSize: 44,
            fontWeight: 700,
            color: 'var(--c-text)',
            letterSpacing: -1.5, lineHeight: 1,
            margin: 0,
          }}>
            Profile
          </h1>
          <button type="button" aria-label="Settings" style={ICON_BTN} onClick={() => navigate(ROUTES.settings)}>
            <Settings size={16} />
          </button>
        </div>

        {/* Profile header card */}
        <div style={{ padding: '24px 24px 0' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 20,
            padding: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 16,
          }}>
            <div style={{ position: 'relative' }}>
              <div style={{
                width: 60, height: 60, borderRadius: 16,
                background: 'var(--c-surface)',
                border: '1px solid var(--c-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                overflow: 'hidden',
              }}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <svg width="36" height="36" viewBox="0 0 32 32" aria-hidden>
                    <rect x="6" y="7"  width="20" height="4" rx="1" fill="#9CC5F0" />
                    <rect x="6" y="14" width="20" height="4" rx="1" fill="#60A5E8" />
                    <rect x="6" y="21" width="20" height="4" rx="1" fill="#2E82D6" />
                  </svg>
                )}
              </div>
              <button
                type="button"
                aria-label="Upload profile photo"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  position: 'absolute',
                  bottom: -4, right: -4,
                  width: 24, height: 24, borderRadius: '50%',
                  background: 'var(--c-text)',
                  border: '2px solid var(--c-bg)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <Camera size={11} color={'#FFFFFF'} />
              </button>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 20,
                fontWeight: 600,
                color: 'var(--c-text)',
                letterSpacing: -0.3,
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}>
                {displayName}
              </div>
              <div style={{
                fontSize: 12,
                color: 'var(--c-text-muted)',
                fontFamily: 'var(--ff-mono)',
                marginTop: 4,
              }}>
                {formatZambiaPhone(profile?.phone) ?? '—'}
              </div>
              <div style={{ marginTop: 8 }}>
                <Pill tone={KYC_TONE[kycStatus]}>
                  <Shield size={11} strokeWidth={2} />
                  {KYC_LABEL[kycStatus]}
                  {kycStatus === 'verified' && score != null
                    ? ` · Tier ${{ new: 1, building: 2, established: 3, strong: 4, excellent: 5 }[score.tier]}`
                    : ''}
                </Pill>
              </div>
            </div>
          </div>
        </div>

        {/* Stat triplet */}
        <div style={{
          padding: '14px 24px 0',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gap: 8,
        }}>
          {[
            { label: 'Loans', value: String(loansCount) },
            { label: 'Score', value: score ? String(score.score) : '—' },
            { label: 'On time', value: onTime },
          ].map((s) => (
            <div key={s.label} style={{
              background: 'var(--c-surface)',
              border: '1px solid var(--c-border)',
              borderRadius: 14,
              padding: 14,
            }}>
              <div style={{
                fontSize: 28,
                fontWeight: 500,
                color: 'var(--c-text)',
                letterSpacing: -0.5,
                lineHeight: 1,
              }}>
                {s.value}
              </div>
              <div className="eyebrow" style={{ fontSize: 11, letterSpacing: 0.6, marginTop: 6 }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Personal details — name edit inline */}
        <div style={{ padding: '0 24px' }}>
          <SectionLabel>Personal details</SectionLabel>
        </div>
        <div style={{ padding: '0 24px' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 18,
            padding: 16,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="eyebrow">Full name</div>
              {editingName ? (
                <input
                  autoFocus
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleSaveName();
                    if (e.key === 'Escape') setEditingName(false);
                  }}
                  style={{
                    fontSize: 15,
                    fontFamily: 'var(--ff-sys)',
                    fontWeight: 500,
                    border: 'none',
                    borderBottom: '2px solid var(--c-accent)',
                    outline: 'none',
                    background: 'transparent',
                    width: '100%',
                    padding: '4px 0',
                    color: 'var(--c-text)',
                  }}
                  aria-label="Edit full name"
                />
              ) : (
                <div style={{ fontSize: 15, color: 'var(--c-text)', fontWeight: 500, marginTop: 2 }}>
                  {profile?.full_name || '—'}
                </div>
              )}
              {profile?.nrc && !editingName && (
                <div style={{
                  fontFamily: 'var(--ff-mono)',
                  fontSize: 11,
                  color: 'var(--c-text-muted)',
                  marginTop: 6,
                }}>
                  NRC · {profile.nrc}
                </div>
              )}
            </div>
            {editingName ? (
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  aria-label="Save name"
                  onClick={() => void handleSaveName()}
                  disabled={savingName}
                  style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: 'var(--c-accent-soft)',
                    border: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Check size={14} color="var(--c-accent)" />
                </button>
                <button
                  type="button"
                  aria-label="Cancel name edit"
                  onClick={() => { setEditingName(false); setNameInput(profile?.full_name ?? ''); }}
                  style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: 'var(--c-danger-bg)',
                    border: 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <X size={14} color="var(--c-danger)" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                aria-label="Edit name"
                onClick={() => { setEditingName(true); setNameInput(profile?.full_name ?? ''); }}
                style={{
                  width: 32, height: 32, borderRadius: 8,
                  background: 'var(--c-surface-alt)',
                  border: 'none',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <Pencil size={13} color="var(--c-text-muted)" />
              </button>
            )}
          </div>
        </div>

        {/* Account list */}
        <div style={{ padding: '0 24px' }}>
          <SectionLabel>Account</SectionLabel>
        </div>
        <div style={{ padding: '0 24px' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 18,
            overflow: 'hidden',
          }}>
            <ListRow
              Icon={UserIcon}
              label="Personal details"
              onClick={() => { setEditingName(true); setNameInput(profile?.full_name ?? ''); }}
            />
            <ListRow
              Icon={CreditCard}
              label="Linked wallets"
              detail="2"
              onClick={() => toast.show('Coming soon', 'info')}
            />
            <ListRow
              Icon={FileText}
              label="Loan agreements"
              onClick={() => navigate(ROUTES.loansList)}
            />
            <ListRow
              Icon={Shield}
              label={kycStatus === 'rejected' ? 'Re-submit documents' : 'Verification'}
              detail={KYC_LABEL[kycStatus]}
              onClick={() => navigate(ROUTES.kyc)}
            />
            <ListRow
              Icon={HelpCircle}
              label="Help & support"
              href="tel:+260977000000"
              isLast
            />
          </div>
        </div>

        {/* Notifications inline */}
        <div style={{ padding: '0 24px' }}>
          <SectionLabel>Notifications</SectionLabel>
        </div>
        <div style={{ padding: '0 24px' }}>
          <div style={{
            background: 'var(--c-surface)',
            border: '1px solid var(--c-border)',
            borderRadius: 18,
            overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 16px',
              borderBottom: '1px solid var(--c-border-lt)',
            }}>
              <span style={{
                width: 32, height: 32, borderRadius: 9,
                background: 'var(--c-surface-alt)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Bell size={16} color="var(--c-text)" />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, color: 'var(--c-text)', fontWeight: 500 }}>
                  Loan reminders
                </div>
                <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 2 }}>
                  SMS alerts 1 and 3 days before repayment
                </div>
              </div>
              <Toggle
                checked={prefs.loanReminders}
                onChange={(v) => updatePref('loanReminders', v)}
                label="Loan reminders"
              />
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '14px 16px',
            }}>
              <span style={{
                width: 32, height: 32, borderRadius: 9,
                background: 'var(--c-surface-alt)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}>
                <Bell size={16} color="var(--c-text)" />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, color: 'var(--c-text)', fontWeight: 500 }}>
                  Promotions
                </div>
                <div style={{ fontSize: 12, color: 'var(--c-text-muted)', marginTop: 2 }}>
                  News about features and loan tiers
                </div>
              </div>
              <Toggle
                checked={prefs.promotions}
                onChange={(v) => updatePref('promotions', v)}
                label="Promotions"
              />
            </div>
          </div>
        </div>

        {/* PWA install */}
        {(canInstall || isInstalled) && (
          <>
            <div style={{ padding: '0 24px' }}>
              <SectionLabel>App</SectionLabel>
            </div>
            <div style={{ padding: '0 24px' }}>
              <div style={{
                background: 'var(--c-surface)',
                border: '1px solid var(--c-border)',
                borderRadius: 18,
                overflow: 'hidden',
              }}>
                {canInstall ? (
                  <ListRow
                    Icon={Smartphone}
                    label="Add to home screen"
                    onClick={() => void handleInstall()}
                    isLast
                  />
                ) : (
                  <ListRow
                    Icon={Check}
                    label="Installed on this device"
                    isLast
                  />
                )}
              </div>
            </div>
          </>
        )}

        {/* Sign out + danger */}
        <div style={{ padding: '24px 24px 0' }}>
          <button
            type="button"
            onClick={() => void handleSignOut()}
            disabled={signingOut}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: 'transparent',
              border: 'none',
              color: 'var(--c-danger)',
              fontFamily: 'var(--ff-sys)',
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              padding: '12px 0',
            }}
          >
            <LogOut size={16} />
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>

        <div style={{ padding: '8px 24px 24px' }}>
          <button
            type="button"
            onClick={() => { setDeleteConfirm(''); setShowDeleteModal(true); }}
            style={{
              background: 'transparent',
              border: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              color: 'var(--c-text-muted)',
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              padding: '8px 0',
            }}
          >
            <Trash2 size={14} />
            Delete account
          </button>
        </div>

        <p style={{
          textAlign: 'center',
          fontSize: 11,
          color: 'var(--c-text-dim)',
          fontFamily: 'var(--ff-mono)',
          margin: '0 0 16px',
        }}>
          Thandizo v{APP_VERSION} ·{' '}
          <a href="#" style={{ color: 'var(--c-text-dim)' }}>Terms</a>{' '}·{' '}
          <a href="#" style={{ color: 'var(--c-text-dim)' }}>Privacy</a>
        </p>
      </main>

      <NavBar />

      {/* Delete account modal */}
      {showDeleteModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-modal-title"
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
          }}
        >
          <div
            style={{
              position: 'absolute', inset: 0,
              background: 'rgba(11,20,38,0.5)',
              backdropFilter: 'blur(2px)',
            }}
            onClick={() => setShowDeleteModal(false)}
          />
          <div style={{
            position: 'relative',
            background: 'var(--c-surface)',
            borderRadius: '24px 24px 0 0',
            padding: '28px 24px 40px',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            maxWidth: 480,
            margin: '0 auto',
            width: '100%',
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: 'var(--c-danger-bg)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <AlertTriangle size={24} color="var(--c-danger)" />
              </div>
              <h2 id="delete-modal-title" style={{
                margin: 0,
                fontSize: 24,
                fontWeight: 700,
                color: 'var(--c-text)',
                letterSpacing: -0.5,
                textAlign: 'center',
              }}>
                Delete your account
              </h2>
            </div>

            <p style={{
              margin: 0,
              fontSize: 14,
              color: 'var(--c-text-sec)',
              lineHeight: 1.6,
              textAlign: 'center',
            }}>
              This will permanently erase your personal details and login access.
              Loan and payment records are retained for <strong>7 years</strong> as required by
              Zambian financial regulations.
            </p>

            <div>
              <label htmlFor="delete-confirm-input" style={{
                display: 'block',
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--c-text-sec)',
                marginBottom: 8,
              }}>
                Type <strong>DELETE</strong> to confirm
              </label>
              <input
                id="delete-confirm-input"
                type="text"
                value={deleteConfirm}
                onChange={(e) => setDeleteConfirm(e.target.value)}
                placeholder="DELETE"
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  borderRadius: 12,
                  border: `1.5px solid ${deleteConfirm === 'DELETE' ? 'var(--c-danger)' : 'var(--c-border)'}`,
                  background: 'var(--c-surface-alt)',
                  fontSize: 15,
                  letterSpacing: 2,
                  fontFamily: 'var(--ff-mono)',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <Button
              block size="lg"
              variant="danger"
              loading={deleting}
              disabled={deleteConfirm !== 'DELETE'}
              onClick={() => void handleDeleteAccount()}
            >
              Delete account
            </Button>
            <Button
              block
              variant="ghost"
              onClick={() => setShowDeleteModal(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
